import { H2, H5, AnchorButton } from '@wheely/ui-kit';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { getPartner } from '@/modules/partner/store/selectors';
import { CountryCode } from '@/shared/models/country-code';
import { CenteringContainer } from '@/shared/view/CenteringContainer';
import { Icon } from '@src-common/view/components/Icon';

import { CardView } from './components/Card';
import s from './styles.module.css';
import type { ICard, TCardStatus } from './types';

interface ITodoListProps {
  cards: ICard[];
  sectionTitles?: Record<TCardStatus, React.ReactNode>;
  hasTitle?: boolean;
  hasProgress?: boolean;
  isPartnerActive?: boolean;
}

const sortedStatuses: TCardStatus[] = ['rejected', 'todo', 'review', 'done'];

const defaultSectionTitles: Record<TCardStatus, React.ReactNode> = {
  done: (
    <FormattedMessage defaultMessage="DONE" id="LyJtaR" description="ToDo list section title" />
  ),
  rejected: (
    <FormattedMessage defaultMessage="REJECTED" id="dVPJv1" description="ToDo list section title" />
  ),
  review: (
    <FormattedMessage
      defaultMessage="IN REVIEW"
      id="7B8OP4"
      description="ToDo list section title"
    />
  ),
  todo: (
    <FormattedMessage defaultMessage="TO DO" id="rAV2s3" description="ToDo list section title" />
  ),
};

const calculateProgress = (cards: ICard[]): number => {
  const completedCount = cards.filter(
    card => !card.isExcludedFromProgress && card.cardStatus === 'done',
  ).length;
  const total = cards.filter(card => !card.isExcludedFromProgress).length;

  return Math.floor((completedCount / total) * 100);
};

const ToDoView: React.FC<ITodoListProps> = memo(
  ({ cards, sectionTitles = defaultSectionTitles, hasTitle, hasProgress }) => {
    const intl = useIntl();
    const links = useExternalLinks();
    const partner = useSelector(getPartner);

    const isGreatBritain = partner?.countryCode === CountryCode.GreatBritain;

    const statuses = new Set(cards.map(card => card.cardStatus));

    const withStatus = (cardStatus: TCardStatus) =>
      cards.filter(card => card.cardStatus === cardStatus);

    return (
      <CenteringContainer>
        {hasTitle && (
          <H2 className={s.title} data-test-id="todo__header">
            {intl.formatMessage({
              defaultMessage: 'TO-DO LIST',
              id: 'Ouf4Zl',
              description: 'ToDo header',
            })}
          </H2>
        )}
        <div className={s.faqSection}>
          <H5 className={s.faqDescription}>
            <FormattedMessage
              defaultMessage="Find useful information about registration, adding the vehicle and more."
              id="T7BLD2"
              description="FAQ link description"
            />
          </H5>
          <AnchorButton
            href={links.faq}
            intent="none"
            minimal={true}
            className={s.faqLink}
            rightIcon={<Icon icon="arrow-to-end-in-circle" size="24px" className={s.faqLinkIcon} />}
          >
            <H5 tag="span">
              <FormattedMessage defaultMessage="Help & FAQ" id="cZqaMX" description="FAQ link" />
            </H5>
          </AnchorButton>
          {isGreatBritain && (
            <AnchorButton
              href={links.becomingChauffeur}
              intent="none"
              minimal={true}
              className={s.faqLink}
              rightIcon={
                <Icon icon="arrow-to-end-in-circle" size="24px" className={s.faqLinkIcon} />
              }
            >
              <H5 tag="span">
                <FormattedMessage
                  defaultMessage="Becoming a Wheely Chauffeur"
                  id="onboarding_intro_title"
                  description="Becoming a wheely chauffeur link"
                />
              </H5>
            </AnchorButton>
          )}
        </div>
        {sortedStatuses.map((cardStatus: TCardStatus) =>
          statuses.has(cardStatus) ? (
            <section
              key={cardStatus}
              className={s.section}
              data-test-id={`todo__section__${cardStatus}`}
            >
              <H5 className={s.sectionTitle} data-test-id="todo__section__title">
                {sectionTitles[cardStatus]}
                {hasProgress && cardStatus === 'todo' && (
                  <span className={s.progress} data-test-id="todo__progress">
                    {intl.formatMessage(
                      {
                        defaultMessage: '{progress}% done',
                        id: 'wPV2Fk',
                        description: 'todo list progress',
                      },
                      {
                        progress: calculateProgress(cards),
                      },
                    )}
                  </span>
                )}
              </H5>
              {withStatus(cardStatus).map(card => (
                <CardView key={card.id} {...card} />
              ))}
            </section>
          ) : null,
        )}
      </CenteringContainer>
    );
  },
);

ToDoView.displayName = 'ToDoView';

export type { ITodoListProps };
export { ToDoView };
